import React, { memo, useCallback } from 'react';
import { Button, Icon, Text, Types } from '@marriott/mi-ui-library';

import { extractIconClass } from '../../modules/utils/memberSnapshotHelper';
import { EmptyStateCardModel } from '../../organisms/WalletAwardsSection/types';
import {
  StyledAwardsContainer,
  StyledDivider,
  StyledEmptyStateCard,
} from '../../organisms/WalletAwardsSection/WalletAwardsSection.styles';
import { WalletAwardsEmptyStateViewProps } from './WalletAwardsEmptyStateView.types';

interface EmptyStateCardProps {
  card: EmptyStateCardModel;
}

const EmptyStateCard: React.FC<EmptyStateCardProps> = memo(({ card }) => {
  const handleCTAClick = useCallback(() => {
    if (card.ctaAction === 'openModal' && card.ctaModalId) {
      // Modal is OOS — wire event for future stories (WEB-199746 / WEB-199769)
      console.log(`Open modal: ${card.ctaModalId}`);
    } else if (card.ctaPath) {
      window.open(card.ctaPath, card.openInNewTab ? '_blank' : '_self');
    }
  }, [card]);

  return (
    <StyledEmptyStateCard
      data-testid={`awards-empty-state-${card.awardType.toLowerCase()}`}
      role="region"
      aria-label={card.title}
    >
      <div className="wallet-awards-section__summary d-flex align-items-center flex-grow-1 w-auto">
        <div className="wallet-awards-section__icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0">
          <Icon
            iconClass={`wallet-awards-section__icon icon-decorative ${extractIconClass(card.iconPath)}`}
            ariaLabel={card.iconAltText || card.title}
          />
        </div>
        <div className="wallet-awards-section__content d-flex flex-column">
          <Text
            element={Types.tags.div}
            fontSize={Types.size.large}
            copyText={card.title}
            customClass="wallet-awards-section__title m-0 t-subtitle-l"
          />
          <Text
            element={Types.tags.paragraph}
            fontSize={Types.size.medium}
            copyText={card.description}
            customClass="wallet-awards-section__description m-0 t-font-m"
          />
        </div>
      </div>
      <Button
        isLink={true}
        type={Types.ButtonTypeVariation.Button}
        className="m-link-tertiary-button wallet-awards-section__cta p-0 ml-md-auto mt-2 mt-md-0 align-self-start flex-shrink-0"
        callback={handleCTAClick}
        buttonCopy={card.ctaLabel}
        ariaLabel={card.ctaAriaLabel}
        testId={`awards-empty-cta-${card.awardType.toLowerCase()}`}
      />
    </StyledEmptyStateCard>
  );
});

export const WalletAwardsEmptyStateView: React.FC<WalletAwardsEmptyStateViewProps> = memo(
  ({
    componentId,
    customClass,
    hideComponent,
    isLoading,
    fnaCard,
    nuaCard,
    shouldShowDivider,
    fnaCount,
    nuaCount,
    activeAwardsCount,
  }) => {
    if (hideComponent || isLoading || (!fnaCard && !nuaCard)) {
      return null;
    }

    return (
      <StyledAwardsContainer
        data-testid={componentId}
        className={`d-flex flex-column w-100 mb-4 ${customClass ?? ''}`}
        data-fna-count={fnaCount}
        data-nua-count={nuaCount}
        data-active-awards-count={activeAwardsCount}
      >
        {fnaCard ? <EmptyStateCard card={fnaCard} /> : null}
        {shouldShowDivider && fnaCard && nuaCard ? <StyledDivider /> : null}
        {nuaCard ? <EmptyStateCard card={nuaCard} /> : null}
      </StyledAwardsContainer>
    );
  }
);
